<script setup>
import { computed } from 'vue';
import { getI18nKey } from 'dashboard/routes/dashboard/settings/helper/settingsHelper';
import ShowMore from 'dashboard/components/widgets/ShowMore.vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  webhook: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['edit', 'delete', 'enable']);
const { t } = useI18n();
const subscribedEvents = computed(() => {
  const { subscriptions } = props.webhook;
  return subscriptions
    .map(event =>
      t(
        getI18nKey(
          'INTEGRATION_SETTINGS.WEBHOOK.FORM.SUBSCRIPTIONS.EVENTS',
          event
        )
      )
    )
    .join(', ');
});

const isDisabled = computed(() => Boolean(props.webhook.disabled_at));
</script>

<template>
  <tr>
    <td class="py-4 ltr:pr-4 rtl:pl-4">
      <div
        class="font-medium break-words text-slate-700 dark:text-slate-100 flex items-center gap-2"
      >
        <span>{{ webhook.url }}</span>
        <span
          v-if="isDisabled"
          class="inline-flex items-center rounded px-2 py-0.5 text-xs font-medium bg-ruby-100 text-ruby-700 dark:bg-ruby-900 dark:text-ruby-200"
        >
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.DISABLED_BADGE') }}
        </span>
      </div>
      <div class="block mt-1 text-sm text-slate-500 dark:text-slate-400">
        <span class="font-medium">
          {{ $t('INTEGRATION_SETTINGS.WEBHOOK.SUBSCRIBED_EVENTS') }}:
        </span>
        <ShowMore :text="subscribedEvents" :limit="60" />
      </div>
      <div
        v-if="isDisabled"
        class="block mt-1 text-xs text-ruby-600 dark:text-ruby-300"
      >
        {{
          $t('INTEGRATION_SETTINGS.WEBHOOK.DISABLED_REASON', {
            count: webhook.consecutive_failures,
            error: webhook.last_error,
          })
        }}
      </div>
    </td>
    <td class="py-4 min-w-xs">
      <div class="flex justify-end gap-1">
        <Button
          v-if="isDisabled"
          v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.ENABLE.BUTTON_TEXT')"
          icon="i-lucide-play"
          emerald
          xs
          faded
          @click="emit('enable', webhook)"
        />
        <Button
          v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.EDIT.BUTTON_TEXT')"
          icon="i-lucide-pen"
          slate
          xs
          faded
          @click="emit('edit', webhook)"
        />
        <Button
          v-tooltip.top="$t('INTEGRATION_SETTINGS.WEBHOOK.DELETE.BUTTON_TEXT')"
          icon="i-lucide-trash-2"
          xs
          ruby
          faded
          @click="emit('delete', webhook, index)"
        />
      </div>
    </td>
  </tr>
</template>
